import {
  CheckCircleOutline,
  Close,
  HighlightOff,
  OpenInFull,
} from "@mui/icons-material"
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"
import { FC, useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { JsonView } from "react-json-view-lite"
import { Sst2Config, Sst2Locker, Sst2TemplateAssociation } from "shared/sstv2Types"
import {
  useGetSstv2LockerQuery,
  usePatchSstv2LockerSqsTagMutation,
} from "src/features/partners/partnersApi"
import { useNotification } from "./NotificationProvider"

interface SaSstv2LockerDetailDrawerProps {
  locker: Sst2Locker | null
  open: boolean
  onClose: () => void
  onLockerUpdated: () => void
}

/** Extract the SQS tag from locker configs (type=operation_mode → value.sqs_tag). */
const extractSqsTag = (configs: Sst2Config[] | null | undefined): string | null => {
  if (!configs || configs.length === 0) return null
  const opMode = configs.find((c) => c.type === "operation_mode")
  if (!opMode) return null
  const val = opMode.value
  if (typeof val === "object" && val !== null && "sqs_tag" in val && typeof val.sqs_tag === "string") {
    return val.sqs_tag
  }
  return null
}

const DetailField: FC<{ label: string; value: string | number | null | undefined }> = ({
  label,
  value,
}) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" fontFamily="monospace">
      {value ?? "-"}
    </Typography>
  </Box>
)

const SectionHeader: FC<{ title: string }> = ({ title }) => (
  <Typography variant="subtitle2" fontWeight="bold" sx={{ mt: 2, mb: 0.5 }}>
    {title}
  </Typography>
)

export const SaSstv2LockerDetailDrawer: FC<SaSstv2LockerDetailDrawerProps> = ({
  locker,
  open,
  onClose,
  onLockerUpdated,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { showNotification } = useNotification()

  // Fresh data fetch when drawer opens
  const {
    data: lockerResp,
    isLoading,
    isFetching,
    refetch,
  } = useGetSstv2LockerQuery(
    { resourceId: locker?.id ?? "" },
    { skip: !open || !locker?.id },
  )

  const freshLocker = lockerResp?.data ?? locker

  // SQS tag update state
  const [newSqsTag, setNewSqsTag] = useState("")
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [updateError, setUpdateError] = useState<string | null>(null)

  const [patchSqsTag, { isLoading: isPatching }] =
    usePatchSstv2LockerSqsTagMutation()

  const currentSqsTag = freshLocker ? extractSqsTag(freshLocker.configs) : null

  // Reset input when locker changes
  useEffect(() => {
    if (open) {
      setNewSqsTag("")
      setUpdateError(null)
    }
  }, [open, locker?.id])

  const handleUpdateClick = useCallback(() => {
    if (!newSqsTag.trim()) return
    setConfirmOpen(true)
  }, [newSqsTag])

  const handleConfirmUpdate = useCallback(async () => {
    if (!freshLocker) return
    setConfirmOpen(false)
    setUpdateError(null)

    try {
      await patchSqsTag({
        lockerId: freshLocker.id,
        sqsTag: newSqsTag.trim(),
      }).unwrap()

      showNotification(t("sqsTagUpdatedSuccess"), "success")
      setNewSqsTag("")
      refetch()
      onLockerUpdated()
    } catch (err: any) {
      const message =
        err?.data?.message || err?.message || t("sqsTagUpdateFailed")
      setUpdateError(message)
    }
  }, [freshLocker, newSqsTag, patchSqsTag, showNotification, t, refetch, onLockerUpdated])

  const isLoadingDetail = isLoading || isFetching
  const canSubmit = newSqsTag.trim().length > 0 && !isPatching && !isLoadingDetail

  return (
    <>
      <Drawer data-ui="UI-SL2W-235"
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: "55vw",
            maxWidth: "900px",
            maxHeight: "calc(100vh - 64px)",
            height: "calc(100vh - 64px)",
            paddingTop: "64px",
          },
        }}
      >
        <Stack direction="column" spacing={1} p={2} height="100%">
          {/* Header */}
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Typography variant="body1" fontWeight="bold">
                {freshLocker?.name || freshLocker?.id || t("lockerDetails")}
              </Typography>
              {freshLocker?.uuid && (
                <Typography variant="caption" fontFamily="monospace">
                  {freshLocker.uuid}
                </Typography>
              )}
              {isLoadingDetail && <CircularProgress size={16} />}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title={t("openFullPage") || "Open full page"}>
                <IconButton
                  onClick={() => {
                    if (freshLocker?.id) {
                      navigate(`/sa/sstv2/lockers/${freshLocker.id}`)
                    }
                  }}
                  disabled={!freshLocker?.id}
                >
                  <OpenInFull />
                </IconButton>
              </Tooltip>
              <IconButton onClick={onClose}>
                <Close />
              </IconButton>
            </Stack>
          </Stack>

          <Divider />

          {/* Body */}
          <Box sx={{ overflow: "auto", flex: 1 }}>
            {isLoading && !freshLocker ? (
              <Stack spacing={2} p={1}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} variant="rectangular" height={32} />
                ))}
              </Stack>
            ) : freshLocker ? (
              <Stack spacing={1} p={1}>
                {/* Identity */}
                <SectionHeader title={t("identity")} />
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <DetailField label="ID" value={freshLocker.id} />
                  <DetailField label="UUID" value={freshLocker.uuid} />
                  <DetailField
                    label={t("lockerNumber")}
                    value={freshLocker.locker_number}
                  />
                  <DetailField
                    label={t("serialNumber")}
                    value={freshLocker.serial_number}
                  />
                  <DetailField
                    label={t("referenceId")}
                    value={freshLocker.reference_id}
                  />
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* Location */}
                <SectionHeader title={t("location")} />
                <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                  <DetailField label={t("address")} value={freshLocker.address} />
                  <DetailField label={t("city")} value={freshLocker.city} />
                  <DetailField label={t("province")} value={freshLocker.region} />
                  <DetailField label={t("country")} value={freshLocker.country} />
                  <DetailField
                    label={t("postalCode")}
                    value={freshLocker.postal_code}
                  />
                  <DetailField
                    label={t("latitude")}
                    value={freshLocker.latitude}
                  />
                  <DetailField
                    label={t("longitude")}
                    value={freshLocker.longitude}
                  />
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* Status */}
                <SectionHeader title={t("status")} />
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {freshLocker.is_online ? (
                      <CheckCircleOutline
                        fontSize="small"
                        sx={{ color: "success.main" }}
                      />
                    ) : (
                      <HighlightOff
                        fontSize="small"
                        sx={{ color: "error.main" }}
                      />
                    )}
                    <Typography variant="body2">
                      {freshLocker.is_online ? t("online") : t("offline")}
                    </Typography>
                  </Stack>
                  <Chip
                    label={
                      freshLocker.is_enabled
                        ? t("enabled")
                        : t("deactivated")
                    }
                    color={freshLocker.is_enabled ? "success" : "default"}
                    size="small"
                    variant="outlined"
                  />
                  {freshLocker.timezone && (
                    <DetailField
                      label={t("timezone")}
                      value={freshLocker.timezone}
                    />
                  )}
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* SQS Tag */}
                <SectionHeader title={t("sqsTag")} />
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      {t("currentSqsTag")}:
                    </Typography>
                    {currentSqsTag ? (
                      <Chip
                        label={currentSqsTag}
                        color="primary"
                        size="small"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        {t("noSqsTag")}
                      </Typography>
                    )}
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      label={t("newSqsTag")}
                      value={newSqsTag}
                      onChange={(e) => setNewSqsTag(e.target.value)}
                      size="small"
                      disabled={isPatching || isLoadingDetail}
                      sx={{ minWidth: 250 }}
                    />
                    <Button
                      variant="contained"
                      size="medium"
                      onClick={handleUpdateClick}
                      disabled={!canSubmit}
                      sx={{ whiteSpace: "nowrap" }}
                    >
                      {isPatching ? (
                        <>
                          <CircularProgress size={16} sx={{ mr: 1 }} />
                          {t("updating")}
                        </>
                      ) : (
                        t("updateSqsTag")
                      )}
                    </Button>
                  </Stack>

                  {updateError && (
                    <Alert severity="error" onClose={() => setUpdateError(null)}>
                      {updateError}
                    </Alert>
                  )}
                </Stack>

                <Divider sx={{ my: 1 }} />

                {/* Doors */}
                <SectionHeader title={t("doors")} />
                {freshLocker.doors && freshLocker.doors.length > 0 ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      {freshLocker.doors.length} {t("doors").toLowerCase()}
                    </Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <strong>ID</strong>
                            </TableCell>
                            <TableCell>
                              <strong>{t("number")}</strong>
                            </TableCell>
                            <TableCell>
                              <strong>{t("reservations")}</strong>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {freshLocker.doors.map((door) => (
                            <TableRow key={door.id}>
                              <TableCell>
                                <Typography
                                  variant="caption"
                                  fontFamily="monospace"
                                >
                                  {door.id}
                                </Typography>
                              </TableCell>
                              <TableCell>{door.door_number}</TableCell>
                              <TableCell>
                                {door.reservations?.length ?? 0}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("noData")}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Configs */}
                <SectionHeader title={t("configs")} />
                {freshLocker.configs && freshLocker.configs.length > 0 ? (
                  <Box
                    sx={{
                      maxHeight: 200,
                      overflow: "auto",
                      bgcolor: "grey.50",
                      borderRadius: 1,
                      p: 1,
                    }}
                  >
                    <JsonView data={freshLocker.configs as any} />
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("noData")}
                  </Typography>
                )}

                <Divider sx={{ my: 1 }} />

                {/* Templates */}
                <SectionHeader title={t("templates")} />
                {freshLocker.templates_association && freshLocker.templates_association.length > 0 ? (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {freshLocker.templates_association.map((tmpl: Sst2TemplateAssociation, i: number) => (
                      <Chip
                        key={i}
                        label={tmpl.name}
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Stack>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {t("noData")}
                  </Typography>
                )}
              </Stack>
            ) : null}
          </Box>
        </Stack>
      </Drawer>

      {/* Confirmation Dialog */}
      <Dialog data-ui="UI-SL2W-236"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("confirmSqsTagUpdate")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("sqsTagUpdateConfirmMessage")}
          </DialogContentText>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <Chip
              label={currentSqsTag || t("noSqsTag")}
              color="default"
              size="small"
              variant="outlined"
            />
            <Typography variant="body2">{"-->"}</Typography>
            <Chip label={newSqsTag} color="primary" size="small" />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t("cancel")}</Button>
          <Button
            onClick={handleConfirmUpdate}
            variant="contained"
            color="primary"
            disabled={isPatching}
          >
            {t("confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default SaSstv2LockerDetailDrawer
